import React from 'react'
import { compose } from 'recompose'
import get from 'lodash/get'
import { BTitle, BForm, BText, BIcon } from '@prague-digi/ui-components'
import { connect } from 'react-redux'

import {
  AddressInputs,
  BCheckbox,
  Notification,
  PageControlButtons,
  PrefillFormButton,
  Title,
} from '../../components'
import messages from '../../i18n'
import {
  errorHandler,
  form,
  FormInterface,
  intl,
  IntlInterface,
  wizard,
  WizardInterface,
} from '../../hoc'
import formValidate from './ClientAddress.validate'
import { clientActions, modalDialogActions } from '../../redux'
import { ReduxState } from '../../types'
import { preformatZip } from '../../utils/helpers'
import { API_ERROR, PERMANENT_ADDRESS_HINT } from '../../constants/modals'
import { ApiErrorInfo, Response } from '../../api/api'

export interface Props extends FormInterface, WizardInterface, IntlInterface {
  getAddressHints: any
  onClose: any
  onContinue: any
  isModal?: boolean
  saveAddresses: any
  showModalDialog: any
  secondaryMessage: any
  submitMessage: any
  storeData: any
}

const FORM_NAME = 'ClientAddress'

export class ClientAddress extends React.Component<Props> {
  constructor(props: Props) {
    super(props)
    props.initialize(props.storeData)
  }

  onSubmit = () => {
    this.props.handleSubmit(this.saveAddressesCallback)()
  }

  onBack = () => {
    if (this.props.onClose) {
      this.props.onClose()
    } else {
      this.props.wizard.gotoPrevious()
    }
  }

  clearContactAddress = (evt: any) => {
    if (evt.target.checked) {
      const { change } = this.props
      change('contactAddress.street', '')
      change('contactAddress.city', '')
      change('contactAddress.landRegistryNumber', '')
      change('contactAddress.houseNumber', '')
      change('contactAddress.zip', '')
    }
  }

  saveAddressesCallback = () => {
    this.props.saveAddresses({
      success: () => {
        if (this.props.onContinue) {
          this.props.onContinue()
        } else {
          this.props.wizard.gotoNext()
        }
      },
      error: (errorInfo: ApiErrorInfo) => {
        if (errorInfo.type === Response.dataError && errorInfo.errors) {
          this.props.setErrors(FORM_NAME, errorInfo.errors)
        } else {
          this.props.showModalDialog(API_ERROR({ errorInfo, onRetry: this.onSubmit }))
        }
      },
    })
  }

  showPermanentAddressHelp = () => {
    this.props.showModalDialog(PERMANENT_ADDRESS_HINT)
  }

  render() {
    const { isModal, invalid, translate } = this.props

    return (
      <BForm id={FORM_NAME}>
        <Title
          center={isModal}
          message={messages.clientAddress.title}
          subTitle={!isModal}
          subTitleMessage={messages.clientAddress.subTitle}
        />
        <BTitle type="h4">
          {translate(messages.clientAddress.permanentAddress)}
          <BIcon
            name="info"
            onClick={this.showPermanentAddressHelp}
            testid="showPermanentAddressHelpBtn"
            theme="ml-2"
          />
        </BTitle>

        <AddressInputs form={FORM_NAME} namePrefix="permanentAddress" />

        <BTitle theme="mt-4" type="h4">
          {translate(messages.clientAddress.contactAddress.title)}
        </BTitle>
        <Notification title={translate(messages.clientAddress.contactAddress.warning)} />
        <BCheckbox
          name="differentContactAddress"
          testid="differentContactAddress"
          onClick={this.clearContactAddress}
          message={
            <div>
              <div>{translate(messages.clientAddress.contactAddress.checkboxText)}</div>
              <BText type="secondary">
                {translate(messages.clientAddress.contactAddress.checkboxSubtext)}
              </BText>
            </div>
          }
        />
        {get(this.props, 'values.differentContactAddress') && (
          <AddressInputs form={FORM_NAME} namePrefix="contactAddress" />
        )}
        <PageControlButtons
          disableSubmit={invalid}
          isModal={isModal}
          onSubmit={this.onSubmit}
          secondaryAction={this.onBack}
        />
        <PrefillFormButton formName="ClientAddress" />
      </BForm>
    )
  }
}

const preformatAddress = (addr: any) => ({ ...addr, zip: preformatZip(addr.zip) })

export default compose(
  errorHandler('containers/ClientAddress/ClientAddress.tsx'),
  wizard,
  connect(
    (state: ReduxState) => ({
      storeData: {
        contactAddress: preformatAddress(state.client.contactAddress),
        differentContactAddress: state.client.differentContactAddress,
        permanentAddress: preformatAddress(state.client.permanentAddress),
      },
    }),
    {
      saveAddresses: clientActions.saveAddresses,
      showModalDialog: modalDialogActions.showModalDialog,
    }
  ),
  form({
    name: FORM_NAME,
    validate: formValidate,
  }),
  intl
)(ClientAddress)
